import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Form from './Form';
import Input from './Input';
import Button from './Button';
import Alert from './Alert';
import { reportLoginFailureIfSystemic } from '../utils/reportLoginFailure';
import { safeNextPath } from '../utils/loginNextPath';

const Login: React.FC = () => {
  const {
    signIn,
    loading,
    error,
    clearError,
    firebaseUser,
    emailVerified,
    sendEmailVerification,
    user,
  } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const nextPath = safeNextPath(searchParams.get('next'));

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [stayLoggedIn, setStayLoggedIn] = useState(true);
  const [localError, setLocalError] = useState('');
  const [showEmailVerificationPrompt, setShowEmailVerificationPrompt] = useState(false);
  const [verificationEmailSent, setVerificationEmailSent] = useState(false);

  // Clear errors when user starts typing (but only if they had content before)
  const [hasTyped, setHasTyped] = useState(false);

  useEffect(() => {
    if (hasTyped && (email || password)) {
      clearError();
      setLocalError('');
    }
  }, [email, password, hasTyped, clearError]);

  // Redirect as soon as we know auth state — don't wait for DB user so login→dashboard feels instant
  useEffect(() => {
    if (firebaseUser) {
      navigate(nextPath || '/dashboard', { replace: true });
    }
  }, [firebaseUser, navigate, nextPath]);

  // Spinner while auth is resolving, and after sign-in until the redirect runs.
  // Otherwise the form paints for a frame between a successful password and the dashboard.
  if (loading || firebaseUser) {
    return (
      <div className="flex items-center justify-center py-16">
        <svg
          className="animate-spin h-8 w-8 text-emerald-400"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          aria-label="Loading"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    setLocalError('');
    setShowEmailVerificationPrompt(false);
    setHasTyped(false); // Reset typing flag on submit

    // Basic validation
    if (!email || !password) {
      setLocalError('Please enter both email and password');
      return;
    }

    if (!email.includes('@')) {
      setLocalError('Please enter a valid email address');
      return;
    }

    try {
      await signIn(email, password, stayLoggedIn);
    } catch (error: unknown) {
      // Clear context error since we're handling it locally
      clearError();
      // Handle specific Firebase auth errors
      const authError = error as { code?: string; message?: string };
      reportLoginFailureIfSystemic(authError.code, authError.message);
      // console.log('Login error:', authError.code, authError.message); // Debug log

      if (
        authError.code === 'auth/user-not-found' ||
        authError.code === 'auth/wrong-password' ||
        authError.code === 'auth/invalid-credential'
      ) {
        setLocalError(
          'The email or password you entered is incorrect. Please check your credentials and try again.'
        );
      } else if (authError.code === 'auth/invalid-email') {
        setLocalError('Please enter a valid email address.');
      } else if (authError.code === 'auth/user-disabled') {
        setLocalError('This account has been disabled. Please contact support.');
      } else if (authError.code === 'auth/too-many-requests') {
        setLocalError('Too many failed login attempts. Please try again later.');
      } else if (authError.code === 'auth/network-request-failed') {
        setLocalError('Network error. Please check your connection and try again.');
      } else {
        // Fallback for any auth error
        setLocalError('Invalid email or password. Please try again.');
      }
    }
  };

  const handleResendVerification = async () => {
    try {
      await sendEmailVerification();
      setVerificationEmailSent(true);
    } catch (error) {
      setLocalError('Failed to send verification email. Please try again.');
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div>
      {showEmailVerificationPrompt ? (
        // Email Verification Prompt
        <div className="text-center">
          <div className="mb-6">
            <div className="w-16 h-16 mx-auto mb-4 bg-electric-500/20 rounded-full flex items-center justify-center">
              <svg
                className="w-8 h-8 text-electric-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.314 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-white mb-2">Email Verification Required</h3>
            <p className="text-gray-100 mb-4">
              Please verify your email address before you can access your account.
            </p>
            <p className="text-sm text-gray-200 mb-6">
              Check your inbox for a verification email from PRAIRI.
            </p>
          </div>

          {verificationEmailSent ? (
            <Alert type="success">Verification email sent! Please check your inbox.</Alert>
          ) : (
            <div className="mb-4">
              <Button onClick={handleResendVerification} disabled={loading}>
                {loading ? 'Sending...' : 'Resend Verification Email'}
              </Button>
            </div>
          )}

          <button
            onClick={() => {
              setShowEmailVerificationPrompt(false);
              // Clear the form and go back to login
              setEmail('');
              setPassword('');
            }}
            className="text-gray-300 hover:text-white transition-colors duration-200 text-sm"
          >
            ← Back to Login
          </button>
        </div>
      ) : (
        // Login Form
        <Form onSubmit={handleSubmit}>
          {/* Email Field */}
          <Input
            type="email"
            value={email}
            onChange={e => {
              setEmail(e.target.value);
              setHasTyped(true);
            }}
            required
            placeholder="Enter your email"
            label="Email Address"
          />

          {/* Password Field */}
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-200 mb-2">
              Password
            </label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={e => {
                  setPassword(e.target.value);
                  setHasTyped(true);
                }}
                required
                className="w-full px-4 py-3 pr-12 bg-gray-800/60 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors duration-200"
                placeholder="Enter your password"
              />
              <button
                type="button"
                onClick={togglePasswordVisibility}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-200 transition-colors duration-200"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21"
                    />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                    />
                  </svg>
                )}
              </button>
            </div>
          </div>

          {/* Stay Logged In Checkbox */}
          <div className="flex items-center">
            <input
              id="stayLoggedIn"
              type="checkbox"
              checked={stayLoggedIn}
              onChange={e => setStayLoggedIn(e.target.checked)}
              className="w-4 h-4 text-gray-400 bg-gray-800/50 border-gray-600 rounded focus:ring-gray-500 focus:ring-2"
            />
            <label htmlFor="stayLoggedIn" className="ml-2 text-sm text-gray-200">
              Stay logged in
            </label>
          </div>

          {/* Error Message */}
          {localError && <Alert type="error">{localError}</Alert>}
          {!localError && error && <Alert type="error">{error}</Alert>}

          {/* Submit Button */}
          <Button type="submit" disabled={loading}>
            {loading ? (
              <div className="flex items-center justify-center">
                <svg
                  className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Signing In...
              </div>
            ) : (
              'Sign In'
            )}
          </Button>

          {/* Forgot Password Link */}
          <div className="text-center">
            <button
              type="button"
              onClick={() => navigate('/password-reset')}
              className="text-gray-300 hover:text-white transition-colors duration-200 text-sm font-medium"
            >
              Forgot my password
            </button>
          </div>
        </Form>
      )}
    </div>
  );
};

export default Login;
